import React, { useEffect, useRef, useState } from "react";
import { Box, Flex, Text, Textarea, IconButton, Icon } from "@chakra-ui/react";
import { HiArrowUp } from "react-icons/hi2";
import { c } from "../../classroomColors";

const Avatar = ({ initial }) => (
  <Flex
    flexShrink={0}
    boxSize="24px"
    borderRadius="full"
    bg={c.surfaceSubtle}
    color={c.textSub}
    align="center"
    justify="center"
    fontSize="10.5px"
    fontWeight="600"
  >
    {initial}
  </Flex>
);

/** One message. Own messages sit right without an avatar, as in the wireframe. */
const Message = ({ message }) => (
  <Flex
    gap="8px"
    mb="12px"
    direction={message.isOwn ? "row-reverse" : "row"}
    align="flex-start"
  >
    <Avatar initial={message.authorInitial} />
    <Box minW={0} maxW="82%" textAlign={message.isOwn ? "right" : "left"}>
      <Text fontSize="12px" fontWeight="500" color={c.text} lineHeight="1.25">
        {message.isOwn ? "You" : message.authorName}
      </Text>
      <Text fontSize="12.5px" color={c.textSub} lineHeight="1.45" wordBreak="break-word">
        {message.text}
      </Text>
    </Box>
  </Flex>
);

/**
 * The Chat panel in the room's dock.
 *
 * Everything said during the class. The question monitor reads the same
 * messages, so a student never has to ask twice in two places.
 */
const SessionChatPanel = ({
  messages,
  typing = [],
  notice,
  onSend,
  readOnly = false,
}) => {
  const [value, setValue] = useState("");
  const scroller = useRef(null);

  // Follow the conversation, as a chat should — and open at the latest
  // message rather than the first.
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, typing.length]);

  const submit = () => {
    const text = value.trim();
    if (!text) return;
    onSend(text);
    setValue("");
  };

  return (
    <Flex direction="column" minH={0} flex="1 1 auto">
      <Box ref={scroller} flex="1 1 auto" minH="140px" overflowY="auto" px="16px" py="10px" data-own-scroll>
        {messages.length === 0 ? (
          <Text fontSize="12.5px" color={c.textMuted} fontWeight="300" textAlign="center" py="20px">
            Nothing said yet.
          </Text>
        ) : (
          messages.map((m) => <Message key={m.id} message={m} />)
        )}
        {notice && (
          <Text fontSize="11px" color={c.textMuted} textAlign="center" py="4px">
            {notice}
          </Text>
        )}
        {typing.length > 0 && (
          <Text fontSize="11.5px" color={c.textMuted} fontStyle="italic">
            {typing.length === 1 ? `${typing[0]} is typing…` : `${typing.length} people are typing…`}
          </Text>
        )}
      </Box>

      {!readOnly && (
        // The composer is a field of its own, white on the glass.
        <Flex
          align="flex-end"
          gap="8px"
          mx="10px"
          mb="10px"
          px="12px"
          py="8px"
          borderRadius="12px"
          bg={c.surface}
          border="1px solid"
          borderColor={c.borderSoft}
        >
          <Textarea
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder="Type here"
            variant="unstyled"
            resize="none"
            rows={1}
            minH="24px"
            maxH="90px"
            fontSize="13px"
            color={c.text}
            _placeholder={{ color: c.textMuted }}
          />
          <IconButton
            aria-label="Send message"
            icon={<Icon as={HiArrowUp} boxSize="14px" />}
            onClick={submit}
            size="xs"
            minW="26px"
            h="26px"
            borderRadius="full"
            bg={value.trim() ? c.solidBg : c.hover}
            color={value.trim() ? c.solidText : c.textMuted}
            _hover={{ opacity: 0.88 }}
            flexShrink={0}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default SessionChatPanel;
